import apiService from '../services/api';

import {
  getEvent,
  getEventListPage,
  getEventSelectedId,
  getOrganiserSelectedId,
  checkEventPageExists
} from '../reducers';

/**
 * Fetch a single event from the API.
 * [Promise middleware](https://github.com/pburtchaell/redux-promise-middleware) dispatches actions describing flow:
 * FETCH_EVENT_PENDING, followed by FETCH_EVENT_FULFILLED or FETCH_EVENT_REJECTED.
 */
export const fetchEvent = id => (dispatch, getState) => dispatch({
  type: 'FETCH_EVENT',
  meta: {
    id,
  },
  /**
   * Since we currently only fetch single events for the 'selected event' detail view,
   * the response (whether fulfilled or rejected) is checked against the then selected ID
   * in order to reject a fulfilled response, or ignore a rejected error response.
   */
  payload: apiService.fetchEvent(id)
    .then((response) => {
      const currentSelectedId = getEventSelectedId(getState());
      return id === currentSelectedId
        ? response // Return reponse in FULFILLED action
        : Promise.reject(); // Reject the promise, handled in following catch
    })
    .catch(error =>
      Promise.reject(
        id === getEventSelectedId(getState())
          ? error // If the event is still selected return the error response in the REJECTED action
          : { cancelled: true }, // Or return a simple object with a flag for reducers to ignore
        ),
    ),
}).catch(error => error); // Promise middleware doesn't catch the rejection, despite actions

/*
 * Fetch multiple events from the API.
 * [Promise middleware](https://github.com/pburtchaell/redux-promise-middleware) dispatches actions describing flow:
 * FETCH_EVENTS_PENDING, followed by FETCH_EVENTS_FULFILLED or FETCH_EVENTS_REJECTED.
 */
export const fetchEvents = (query, append = false) => (dispatch, getState) => dispatch({
  type: 'FETCH_EVENTS',
  meta: {
    append,
    query,
    defaultFetchedBehaviour: true
  },
  payload: apiService[query.name ? 'searchEvents' : 'fetchEvents']({
    ...query,
    page: append ? getEventListPage(getState(), query) : 1,
  }),
}).catch(error => error); // Promise middleware doesn't catch the rejection, despite actions

export const fetchEventsPaged = (query, page) => (dispatch, getState) => {
  if (checkEventPageExists(getState(), query, page)) {
    return dispatch({
      type: 'SET_CURRENT_EVENTS_PAGE',
      meta: { query },
      payload: { page },
    });
  } else {
    return dispatch({
      type: 'FETCH_EVENTS',
      meta: {
        append: page !== 1,
        query,
        page,
        defaultFetchedBehaviour: false
      },
      payload: apiService.fetchEvents({
        ...query,
        page,
      }),
    });
  }
};

/**
 * Add a user to the saves list of an Event
 */
export const addUserToEventSaves = (eventId, userId) => (dispatch, getState) => {
  const event = getEvent(getState(), eventId);
  if (!event) return;
  dispatch({
    type: 'UPDATE_EVENT',
    meta: {
      id: eventId,
    },
    payload: {
      saves: {
        total: event.saves && event.saves.total >= 0
          ? event.saves.total + 1
          : 1,
        users: event.saves && event.saves.users
          ? event.saves.users.concat({ id: userId })
          : [{ id: userId }],
      },
    },
  });
};

/**
 * Remove a user from the saves list of an Event
 */
export const removeUserFromEventSaves = (eventId, userId) => (dispatch, getState) => {
  const event = getEvent(getState(), eventId);
  if (!event) return;
  dispatch({
    type: 'UPDATE_EVENT',
    meta: {
      id: eventId,
    },
    payload: {
      saves: {
        total: event.saves && event.saves.total >= 1
          ? event.saves.total - 1
          : 0,
        users: event.saves && event.saves.users
          ? event.saves.users.filter(user => user.id !== userId)
          : [],
      },
    },
  });
};

/**
  * Fetch a single organiser from the API.
  * [Promise middleware](https://github.com/pburtchaell/redux-promise-middleware) dispatches actions describing flow:
  * FETCH_ORGANISER_PENDING, followed by FETCH_ORGANISER_FULFILLED or FETCH_ORGANISER_REJECTED.
 */
export const fetchOrganiser = id => (dispatch, getState) => dispatch({
  type: 'FETCH_ORGANISER',
  meta: {
    id,
  },
  /**
   * Since we currently only fetch single organisers for the 'selected organiser' detail view,
   * the response (whether fulfilled or rejected) is checked against the then selected ID
   * in order to reject a fulfilled response, or ignore a rejected error response.
   */
  payload: apiService.fetchOrganiser(id)
    .then((response) => {
      const currentSelectedId = getOrganiserSelectedId(getState());
      return id === currentSelectedId
        ? response // Return reponse in FULFILLED action
        : Promise.reject(); // Reject the promise, handled in following catch
    })
    .catch(error =>
      Promise.reject(
        id === getOrganiserSelectedId(getState())
          ? error // If the organiser is still selected return the response in the REJECTED action
          : { cancelled: true }, // Or return a simple object with a flag for reducers to ignore
        ),
    ),
}).catch(error => error); // Promise middleware doesn't catch the rejection, despite actions

export const setHoveredEvent = id => ({
  type: 'EVENT_HOVERED',
  id
});

export const toggleMapView = () => ({ type: 'TOGGLE_MAP_STATE' });



// WEBPACK FOOTER //
// ./app/src/state/actions/index.js